import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Hero } from "../components/Hero";
import { CinematicVideo } from "../components/CinematicVideo";
import { EditorialMarquee } from "../components/EditorialMarquee";
import { TumorGuide } from "../components/TumorGuide";
import { TreatmentChapters } from "../components/TreatmentChapters";
import { StatsBand } from "../components/StatsBand";
import { TransitionBand } from "../components/TransitionBand";
import { Hospitals } from "../components/Hospitals";
import { Process } from "../components/Process";
import { BudgetWizard } from "../components/BudgetWizard";
import { VaccineSpotlight } from "../components/VaccineSpotlight";
import { VideoBand } from "../components/VideoBand";
import { ClinicBand } from "../components/ClinicBand";
import { TestimonialsSection } from "../components/TestimonialsSection";
import { PatientStory } from "../components/PatientStory";
import { NewsSection } from "../components/NewsSection";
import { SecondOpinionForm } from "../components/SecondOpinionForm";
import { FaqSection } from "../components/FaqSection";
import { DocumentsSection } from "../components/DocumentsSection";
import { MagazineShowcase } from "../components/MagazineShowcase";
import { ZarafetBand } from "../components/ZarafetBand";
import { BlackDivider } from "../components/BlackDivider";
import { Footer } from "../components/Footer";
import { AmbientLife } from "../components/AmbientLife";
import { SEO } from "../components/SEO";
import { useLang } from "../i18n/LanguageContext";
import { FAQS } from "../content/siteContent";
import { locFaq } from "../content/i18n_extra";
import { API } from "../lib/api";
import { EnsoCircle } from "../components/JapaneseMotifs";
import { IntroGate } from "../components/IntroGate";

function ManifestoBand() {
  const { lang } = useLang();
  const text = lang === "tr"
    ? "Japonya'da doğrudan tedavi başvurusu yapılamaz; akredite bir aracı kurum şarttır. Biz, I-Cell Networks'ün Türkiye'deki münhasır temsilcisiyiz."
    : "In Japan, international patients cannot apply directly; an accredited coordinator is mandatory. We are I-Cell Networks' exclusive representative in Türkiye.";
  return (
    <section className="relative bg-blush py-24 sm:py-32 border-b border-black/5 overflow-hidden">
      <EnsoCircle className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[380px] h-[380px] text-brand opacity-[0.08] pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-6 text-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <Quote size={36} className="mx-auto text-brand/30" />
          <p className="mt-6 font-heading text-2xl sm:text-4xl 2xl:text-5xl font-bold tracking-tight text-ink leading-snug text-balance" data-testid="manifesto-quote">{text}</p>
        </motion.div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  const { t, lang } = useLang();
  const [dynFaq, setDynFaq] = useState([]);

  useEffect(() => {
    // Otonom SEO motorunun gerçek ziyaretçi sorularından ürettiği dinamik SSS
    let live = true;
    fetch(`${API}/seo/faq`)
      .then((r) => r.json())
      .then((d) => { if (live && Array.isArray(d.items)) setDynFaq(d.items); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      ...FAQS.map((f, i) => {
        const c = locFaq(f, i, lang);
        return { "@type": "Question", name: c.q, acceptedAnswer: { "@type": "Answer", text: c.a } };
      }),
      ...dynFaq.map((f) => ({
        "@type": "Question",
        name: lang === "tr" ? f.q_tr : f.q_en || f.q_tr,
        acceptedAnswer: { "@type": "Answer", text: lang === "tr" ? f.a_tr : f.a_en || f.a_tr },
      })),
    ],
  };
  const physicianLd = {
    "@context": "https://schema.org",
    "@type": "Physician",
    name: "Dr. Mehmet Akdemir",
    jobTitle: "Anestezi ve Reanimasyon Uzmanı",
    description: "Tıbbi mimar ve vizyoner: CybernicX (Cyber Robotic Center) kurucusu, CYBERDYNE Inc. iş ortağı. İnsan 2.0 vizyonu; kişiye özel kanser aşıları ve hücresel tedavilerde Japonya-Türkiye köprüsü.",
    worksFor: { "@type": "Organization", name: "2D Healthcare" },
    email: "drmakdemir@gmail.com",
    telephone: "+90 531 670 24 60",
    nationality: "TR",
    knowsAbout: ["kişiye özel kanser aşısı", "sibertıp", "nörorehabilitasyon", "HAL robotik dış iskelet", "hücresel tedavi", "kişiselleştirilmiş tıp"],
  };
  return (
    <div data-testid="landing-page">
      <SEO
        title={t("seo_title")}
        description={t("seo_desc")}
        keywords="japonyada kanser tedavisi, japonya kanser tedavisi, CAR-T, iPSC, BNCT, ağır iyon tedavisi, proton tedavisi, kanser aşısı, yurtdışında kanser tedavisi, ikinci görüş"
        path="/"
        jsonLd={[faqLd, physicianLd]}
      />
      <IntroGate />
      <Navbar />
      <Hero />
      <CinematicVideo />
      <VaccineSpotlight />
      <ZarafetBand />
      <VideoBand />
      <EditorialMarquee />
      <ManifestoBand />
      <BlackDivider kanji="心" line="Şifa, acele etmeyenlerin sanatıdır." />
      <TumorGuide />
      <TreatmentChapters />
      <StatsBand />
      <TransitionBand />
      <Hospitals />
      <ClinicBand />
      <Process />
      <BlackDivider kanji="命" line="Her detay, sizin için önceden düşünüldü." />
      <BudgetWizard />
      <TestimonialsSection />
      <PatientStory />
      <NewsSection />
      <BlackDivider kanji="道" line="Bilgi, zarafetle paylaşılır." />
      <SecondOpinionForm />
      <FaqSection extra={dynFaq} />
      <BlackDivider kanji="絆" line="Sorularınız asla cevapsız kalmaz." />
      <DocumentsSection />
      <MagazineShowcase />
      <Footer />
      <AmbientLife />
    </div>
  );
}
